import {
  Panel,
  Label,
  HelperText,
  RadioButton,
  FormControl,
} from '@uhg-harmony/uitk-react';
import React, { ChangeEvent } from 'react';
import { Arrow } from 'builder/scenes/SiteBuilder/SettingsPanel/SettingsPanel';
import {
  SizeLocationData,
  WidgetSizeLocation,
} from '../../../builder/components/WidgetSizeLocation';
import { Config, UploadMode } from './types';
import {
  SizeLocationPanel,
  StyledPanel,
  StyledPanelTitle,
} from '../Text/TextWidgetControl';
import S3UploadFileButton from 'builder/components/S3UploadFileButton/S3UploadFileButton';
import { UploadedFile } from '../shared/types';
import { JSON_REGEX, widgetTypes } from 'builder/util/constants';
import { addChild } from 'shared/state/ducks/sites';
import { useDispatch } from 'react-redux';
import uuidv4 from 'uuid/v4';
import { widgetSchema } from 'shared/widgets';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { validateSwaggerSpec, getUploadHelperText } from './uploadwidget.util';

const StyledRadio = styled(RadioButton)`
  & > label {
    font-family: OptumSans, sans-serif;
  }
`;

const UploadWidgetControl: React.FC<WidgetControlProps<Config>> = (
  props: WidgetControlProps<Config>
) => {
  const {
    value,
    onChange,
    widgetState,
    site,
    currentPage,
    closeConfigPanel,
  } = props;
  const updateWidgetSizeLocation = (sizeLoc: SizeLocationData) => {
    onChange({
      ...value,
      width: sizeLoc.width,
      height: sizeLoc.height,
      xLocation: sizeLoc.xLocation,
      yLocation: sizeLoc.yLocation,
      uniqueId: sizeLoc.uniqueId,
    });
  };

  const dispatch = useDispatch();

  /** Handles switching between Horizon Widget and Swagger upload modes. */
  const handleUploadModeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newMode = e.target.value as UploadMode;
    onChange({
      ...value,
      uploadMode: newMode,
      uploadedFile: { name: '', url: '' },
    });
  };

  const handleUpload = (file: UploadedFile) => {
    const { onChange } = props;
    const { config } = props.widgetState;
    onChange(
      {
        ...config,
        uploadedFile: file,
      },
      props.widgetState.id
    );

    if (value.uploadMode === 'swagger') {
      handleSwaggerUpload(file);
    } else {
      addWidget(file);
    }
  };

  /**
   * Handles Swagger/OAS JSON upload: validates the spec and stores the URL
   * in the widget config for runtime rendering via swagger-ui-react.
   */
  const handleSwaggerUpload = async (file: UploadedFile) => {
    const result = await validateSwaggerSpec(file.url);
    if (!result.valid) {
      toast.error(
        `Not a valid OpenAPI specification: ${result.errors?.[0] ??
          'Unknown error'}`
      );
    } else {
      toast.success('Swagger specification imported!');
    }
  };

  const deleteWidget = (widgetId: string) => {
    closeConfigPanel(false);
    props.removeWidget(widgetId);
  };

  const addWidget = async (file: UploadedFile) => {
    const widgetId = uuidv4();
    var Validator = require('jsonschema').Validator;
    var v = new Validator();
    // const ajv = new Ajv();
    try {
      const fetchResponse = await fetch(file.url);
      var widget = await fetchResponse.json();
    } catch (err) {
      console.error('Error - ', err);
    }

    const validate = v.validate(widget, widgetSchema);

    if (!validate.valid || validate.instance === undefined) {
      toast.error('Not a valid widget');
    } else {
      toast.success('Widget Imported!');

      const uploadWidget = {
        width: widgetState.config.width,
        height: widgetState.config.height,
        xLocation: widgetState.config.xLocation,
        yLocation: widgetState.config.yLocation,
      };

      if (
        widget.type === widgetTypes.Section ||
        widget.type === widgetTypes.Card ||
        widget.type === widgetTypes.AccordionWidget ||
        widget.type === widgetTypes.Tabs ||
        widget.type === widgetTypes.Form ||
        widget.type === widgetTypes.Banner ||
        widget.type === widgetTypes.Slideshow
      ) {
        let widgetXLocation = widget.config.xLocation;
        let widgetYLocation = widget.config.yLocation;
        widget.config.xLocation = uploadWidget.xLocation;
        widget.config.yLocation = uploadWidget.yLocation;
        dispatch(
          addChild(
            site.siteId,
            currentPage.pageId,
            'root',
            widget.type,
            widget.config,
            0,
            widgetId
          )
        );
        let childWidgetIds = [];
        widget.childArray.map(child => {
          const childWidgetId = uuidv4();
          childWidgetIds.push(childWidgetId);
          child.config.xLocation =
            uploadWidget.xLocation + (child.config.xLocation - widgetXLocation);
          child.config.yLocation =
            uploadWidget.yLocation + (child.config.yLocation - widgetYLocation);
          dispatch(
            addChild(
              site.siteId,
              currentPage.pageId,
              widgetId,
              child.type,
              child.config,
              0,
              childWidgetId,
              child.tabIndex
            )
          );
        });
        widget.children = childWidgetIds;
      } else {
        widget.config.width = uploadWidget.width;
        widget.config.height = uploadWidget.height;
        widget.config.xLocation = uploadWidget.xLocation;
        widget.config.yLocation = uploadWidget.yLocation;
        dispatch(
          addChild(
            site.siteId,
            currentPage.pageId,
            'root',
            widget.type,
            widget.config,
            0,
            widgetId
          )
        );
      }
      deleteWidget(props.widgetState.id);
    }
  };

  return (
    <>
      <Panel.Group>
        <StyledPanel>
          <Panel
            title={<Label style={StyledPanelTitle}>General</Label>}
            icon={Arrow}
            data-test-id="general-image-panel"
          >
            <Label style={{ fontFamily: 'OptumSans' }}>Upload Mode</Label>
            <FormControl id="upload-mode-radio-group">
              <StyledRadio
                data-test-id="radio-horizon"
                name="upload-mode-radio-group"
                value="horizon"
                onChange={handleUploadModeChange}
                checked={value.uploadMode === 'horizon'}
              >
                Horizon Widget
              </StyledRadio>
              <StyledRadio
                data-test-id="radio-swagger"
                name="upload-mode-radio-group"
                value="swagger"
                onChange={handleUploadModeChange}
                checked={value.uploadMode === 'swagger'}
              >
                Swagger
              </StyledRadio>
            </FormControl>

            <Label style={{ fontFamily: 'OptumSans', marginTop: '1rem' }}>
              Select file to Upload
            </Label>
            <HelperText style={{ fontFamily: 'OptumSans' }}>
              {getUploadHelperText(value.uploadMode)}
            </HelperText>
            <div style={{ position: 'relative', zIndex: 1 }}>
              <S3UploadFileButton
                label="Click to Upload"
                accept={'application/json'}
                onUpload={handleUpload}
                file={widgetState.config.uploadedFile}
                icon={'Upload'}
                iconProps={{
                  width: '1.2em',
                  height: '1.3em',
                  viewBox: '0 0 24 24',
                }}
                fileFormatRegex={JSON_REGEX}
                data-test-id={`file-uploader`}
              />
            </div>
            <HelperText>
              {widgetState.config.uploadedFile
                ? widgetState.config.uploadedFile?.name
                : ''}
            </HelperText>
          </Panel>
        </StyledPanel>
        <StyledPanel>
          <SizeLocationPanel
            title={<Label style={StyledPanelTitle}>Size and Location</Label>}
            initiallyClosed
            icon={Arrow}
            data-test-id="size-and-location-panel"
          >
            <WidgetSizeLocation
              data-test-id="widget-size-location"
              sizeLocation={{
                xLocation: value.xLocation,
                yLocation: value.yLocation,
                width: value.width,
                height: value.height,
                uniqueId: value.uniqueId,
              }}
              updateWidgetSizeLocation={updateWidgetSizeLocation}
              activeWidgetId={widgetState.id}
            />
          </SizeLocationPanel>
        </StyledPanel>
      </Panel.Group>
    </>
  );
};

export default UploadWidgetControl;
